import type {
  ApolloUsageStats,
  EnrichPeopleRequest,
  EnrichPeopleResult,
  PersonActivitiesResponse,
  PersonProfile,
  WorkspaceList,
  WorkspaceListInput,
  DirectoryPerson,
  PersonSearchFilters,
  PersonSearchResponse,
  SavePeopleRequest,
  SavePeopleResult,
  SavedPersonSearch,
  SavedPersonSearchInput,
  SequenceEnrollment,
} from "@sales-ops-amplify/shared";
import { getAccessToken } from "@/stores/authStore";
import { apiDelete, apiGet, apiPatch, apiPost } from "./client";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";

export function getPeopleUsage(): Promise<ApolloUsageStats> {
  return apiGet<ApolloUsageStats>("/api/people/usage");
}

export function getPeopleTechnologies(): Promise<{ technologies: string[] }> {
  return apiGet("/api/people/technologies");
}

export function getPersonProfile(personId: string): Promise<PersonProfile> {
  return apiGet<PersonProfile>(`/api/people/${encodeURIComponent(personId)}`);
}

export function getPersonActivities(
  personId: string,
  params?: { types?: string[]; limit?: number },
): Promise<PersonActivitiesResponse> {
  const q = new URLSearchParams();
  if (params?.types?.length) q.set("types", params.types.join(","));
  if (params?.limit != null) q.set("limit", String(params.limit));
  const qs = q.toString();
  return apiGet<PersonActivitiesResponse>(
    `/api/people/${encodeURIComponent(personId)}/activities${qs ? `?${qs}` : ""}`,
  );
}

export function listPersonSequenceEnrollments(leadId: string): Promise<{
  enrollments: Array<SequenceEnrollment & { sequenceName?: string }>;
}> {
  return apiGet(
    `/api/sequences/enrollments?leadId=${encodeURIComponent(leadId)}`,
  );
}

export function searchPeople(
  filters: PersonSearchFilters,
): Promise<PersonSearchResponse> {
  return apiPost<PersonSearchResponse>("/api/people/search", filters);
}

export function savePeople(body: SavePeopleRequest): Promise<SavePeopleResult> {
  return apiPost<SavePeopleResult>("/api/people/save", body);
}

export function enrichPeople(
  body: EnrichPeopleRequest,
): Promise<EnrichPeopleResult> {
  return apiPost<EnrichPeopleResult>("/api/people/enrich", body);
}

/** Export directory Person search results as CSV. */
export async function exportPeopleCsv(
  people: DirectoryPerson[],
): Promise<Blob> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}/api/people/export`, {
    method: "POST",
    headers,
    body: JSON.stringify({ people }),
  });
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(
      typeof errBody === "object" && errBody && "error" in errBody
        ? String((errBody as { error: string }).error)
        : "Export failed",
    );
  }
  return res.blob();
}

export function listWorkspaceLists(): Promise<{ lists: WorkspaceList[] }> {
  return apiGet("/api/people/lists");
}

export function createWorkspaceList(
  input: WorkspaceListInput,
): Promise<WorkspaceList> {
  return apiPost("/api/people/lists", input);
}

export function deleteWorkspaceList(listId: string): Promise<void> {
  return apiDelete(`/api/people/lists/${listId}`);
}

export function listSavedSearches(): Promise<{ searches: SavedPersonSearch[] }> {
  return apiGet("/api/people/saved-searches");
}

export function createSavedSearch(
  input: SavedPersonSearchInput,
): Promise<SavedPersonSearch> {
  return apiPost("/api/people/saved-searches", input);
}

export function updateSavedSearch(
  searchId: string,
  input: Partial<SavedPersonSearchInput>,
): Promise<SavedPersonSearch> {
  return apiPatch(`/api/people/saved-searches/${searchId}`, input);
}

export function deleteSavedSearch(searchId: string): Promise<void> {
  return apiDelete(`/api/people/saved-searches/${searchId}`);
}
